import type { UpdatesSection } from '@/hooks/useOrderUpdates';
import type { UpdateCall, UpdateComment, UpdateWhatsApp } from '@/types/orderUpdates';
import { updateDayKey } from './updatesFormat';

export type TimelineItem =
  // `run`: this call plus the identical attempts right before it (see collapseCallRuns).
  | { kind: 'calls'; key: string; at: string; call: UpdateCall; run: UpdateCall[] }
  | { kind: 'comments'; key: string; at: string; comment: UpdateComment }
  | { kind: 'whatsapp'; key: string; at: string; message: UpdateWhatsApp };

export interface TimelineDay {
  key: string;
  at: string;
  relative: 'today' | 'yesterday' | null;
  items: TimelineItem[];
}

export interface TimelineLists {
  calls: UpdateCall[];
  comments: UpdateComment[];
  whatsapp: UpdateWhatsApp[];
}

// Same-minute ties: a call usually triggers the comment / message after it.
const KIND_ORDER: Record<UpdatesSection, number> = { calls: 0, comments: 1, whatsapp: 2 };
const DAY_MS = 86_400_000;
// Iraq has kept UTC+3 all year since 2008, so Baghdad's calendar day needs no tz database.
const BAGHDAD_OFFSET_MS = 3 * 3_600_000;

const atOf = (at: unknown) => (typeof at === 'string' ? at : '');

/**
 * One list from the three sources, newest first. Every `at` is Baghdad wall
 * time with the same +03:00 offset, so the ISO strings sort as text. The sort
 * is stable: rows of one source keep the API's own order on a tie.
 */
export const mergeTimeline = ({ calls, comments, whatsapp }: TimelineLists): TimelineItem[] =>
  [
    ...calls.map((call, i) => ({
      kind: 'calls' as const,
      key: `call-${call.id}-${i}`,
      at: atOf(call.at),
      call,
      run: [call],
    })),
    ...comments.map((comment, i) => ({
      kind: 'comments' as const,
      key: `comment-${comment.id}-${i}`,
      at: atOf(comment.at),
      comment,
    })),
    ...whatsapp.map((message, i) => ({
      kind: 'whatsapp' as const,
      key: `wa-${message.id}-${i}`,
      at: atOf(message.at),
      message,
    })),
  ].sort((a, b) =>
    a.at === b.at ? KIND_ORDER[a.kind] - KIND_ORDER[b.kind] : a.at < b.at ? 1 : -1,
  );

const runKey = (call: UpdateCall) =>
  [call.actor, call.outcome, call.label?.en ?? call.label?.ar ?? ''].join('|');

/**
 * Fold back-to-back calls of one day with the same actor, outcome and label
 * into one row ("Line busy ×12"): a real order had 12 busy attempts in a row,
 * which listed flat made the tab ~3400 px tall. Any other row in between
 * breaks the run, so the timeline stays truthful about what happened when.
 */
export const collapseCallRuns = (items: TimelineItem[]): TimelineItem[] =>
  items.reduce<TimelineItem[]>((out, item) => {
    const last = out[out.length - 1];
    if (
      item.kind === 'calls' &&
      last?.kind === 'calls' &&
      updateDayKey(last.at) === updateDayKey(item.at) &&
      runKey(last.call) === runKey(item.call)
    ) {
      out[out.length - 1] = { ...last, run: [...last.run, item.call] };
    } else out.push(item);
    return out;
  }, []);

/** Today's calendar day in Baghdad ("2026-10-01"), whatever the viewer's zone. */
export const baghdadDayKey = (nowMs: number) =>
  new Date(nowMs + BAGHDAD_OFFSET_MS).toISOString().slice(0, 10);

const previousDay = (dayKey: string) => {
  const ms = Date.parse(`${dayKey}T00:00:00Z`);
  return Number.isNaN(ms) ? '' : new Date(ms - DAY_MS).toISOString().slice(0, 10);
};

/** Consecutive rows of one calendar day, labelled Today / Yesterday where that applies. */
export const groupByDay = (items: TimelineItem[], todayKey: string): TimelineDay[] => {
  const yesterdayKey = previousDay(todayKey);
  return items.reduce<TimelineDay[]>((days, item) => {
    const key = updateDayKey(item.at);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(item);
    else
      days.push({
        key,
        at: item.at,
        relative: key === todayKey ? 'today' : key === yesterdayKey ? 'yesterday' : null,
        items: [item],
      });
    return days;
  }, []);
};
